import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import LegalFooter from "@/components/app/LegalFooter";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ageOk, setAgeOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ageOk) { setError("Please confirm you are 18 or over"); return; }
    if (!termsOk) { setError("Please accept the Terms and Privacy Policy to continue"); return; }
    setLoading(true);
    try {
      // If they landed here via a share-card link, pass the referral code along.
      let referral_code = null;
      try { referral_code = localStorage.getItem("fm_ref") || null; } catch { /* ignore */ }
      const u = await register(email, password, name, {
        age_confirmed: ageOk,
        terms_accepted: termsOk,
        referral_code,
      });
      // Consume the ref cookie so a follow-on signup doesn't get attributed to the same referrer
      try { localStorage.removeItem("fm_ref"); } catch { /* ignore */ }
      toast.success(`Welcome to Footymatch, ${u.name}`);
      // Ad-campaign traffic (?src=ad or any utm_source) sees the "You're in!"
      // confirmation page before onboarding — doubles as the conversion URL
      // for the ad platform. Organic sign-ups go straight to onboarding.
      let fromAd = false;
      try {
        const params = new URLSearchParams(window.location.search);
        fromAd = params.get("src") === "ad" || !!params.get("utm_source");
      } catch { /* ignore */ }
      nav(fromAd ? "/welcome" : "/onboarding", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-footy-bg grain">
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <img src={LOGO} alt="" className="w-14 h-14 rounded-full" />
            <span className="font-display text-3xl tracking-wider text-white">FOOTYMATCH</span>
          </Link>
          <div className="footy-card p-7">
            <h1 className="font-display text-4xl uppercase tracking-tight text-white">Join the terrace</h1>
            <p className="text-slate-400 mt-2 text-sm">Create your account. Same passion. Same values.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="footy-label">Your name</label>
                <input required value={name} onChange={(e)=>setName(e.target.value)}
                       placeholder="Jamie" className="footy-input" data-testid="register-name" />
              </div>
              <div>
                <label className="footy-label">Email</label>
                <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                       placeholder="you@example.com" className="footy-input" data-testid="register-email" />
              </div>
              <div>
                <label className="footy-label">Password</label>
                <input type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)}
                       placeholder="At least 6 characters" className="footy-input" data-testid="register-password" />
              </div>

              <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={ageOk} onChange={(e)=>setAgeOk(e.target.checked)}
                       className="w-5 h-5 mt-0.5 accent-footy-red flex-none" data-testid="register-age-checkbox" />
                <span>I confirm I am <span className="font-bold text-white">18 or over</span>.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={termsOk} onChange={(e)=>setTermsOk(e.target.checked)}
                       className="w-5 h-5 mt-0.5 accent-footy-red flex-none" data-testid="register-terms-checkbox" />
                <span>
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-footy-red font-semibold hover:text-footy-redHover" data-testid="register-terms-link">Terms</Link>
                  {", "}
                  <Link to="/privacy" target="_blank" className="text-footy-red font-semibold hover:text-footy-redHover" data-testid="register-privacy-link">Privacy Policy</Link>
                  {" and "}
                  <Link to="/guidelines" target="_blank" className="text-footy-red font-semibold hover:text-footy-redHover" data-testid="register-guidelines-link">Community Guidelines</Link>.
                </span>
              </label>

              {error && <div className="text-sm text-footy-red bg-footy-red/10 border border-footy-red/30 rounded-lg px-3 py-2" data-testid="register-error">{error}</div>}
              <button type="submit" disabled={loading || !ageOk || !termsOk} className="footy-btn-primary w-full disabled:opacity-50" data-testid="register-submit">
                {loading ? "Creating account..." : "Create account"}
              </button>
            </form>
            <p className="text-sm text-slate-400 mt-6 text-center">
              Already have an account? <Link to="/login" className="text-footy-red font-semibold" data-testid="link-login">Log in</Link>
            </p>
          </div>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
